import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext } from "@hello-pangea/dnd";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import SectionCard from "../components/SectionCard";
import Toast from "../components/Toast";
import ProfileModal from "../components/ProfileModal";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import { LogOut, User, Home } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const EmergencyInfoFormPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { t } = useLanguage();
  const scrollContainerRef = React.useRef(null);
  const [sections, setSections] = useState([]);
  const [fieldSuggestions, setFieldSuggestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [showProfileModal, setShowProfileModal] = useState(false);

  const showToast = (message) => {
    setToast({ visible: true, message });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.getManageEmergencyInfo(locketId, token);
      } else {
        res = await api.get(`/emergency/${locketId}/manage`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      // Sort sections by priority (high first), then by order to ensure medical info appears first
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const sortedSections = (res.data.sections || []).sort((a, b) => {
        const priorityDiff = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
        if (priorityDiff !== 0) return priorityDiff;
        return a.order - b.order;
      });
      setSections(sortedSections);
      setFieldSuggestions(res.data.fieldSuggestions || {});
    } catch (err) {
      setError("Failed to load emergency info.");
    } finally {
      setLoading(false);
    }
  }, [locketId, token]);

  useEffect(() => {
    if (!token) {
      navigate(`/${locketId}/edit`);
    }
  }, [token, locketId, navigate]);

  useEffect(() => {
    if (!token) return;
    fetchData();
  }, [token, fetchData]);

  // Listen for token expiry events from axios interceptor
  useEffect(() => {
    const handleTokenExpired = () => {
      logout();
      navigate(`/${locketId}/edit`);
    };

    window.addEventListener("tokenExpired", handleTokenExpired);
    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpired);
    };
  }, [logout, navigate, locketId]);

  const handleAddItem = async (category, key, value) => {
    try {
      await api.post(
        `/emergency/${locketId}/item`,
        {
          key,
          value,
          category,
          isVisible: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchData();
    } catch (err) {
      console.error("Add item error:", err);
      throw err;
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await api.put(`/emergency/${locketId}/item/${itemId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchData();
    } catch (err) {
      console.error("Update item error:", err);
      throw err;
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/emergency/${locketId}/item/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchData();
    } catch (err) {
      console.error("Delete item error:", err);
      throw err;
    }
  };

  const handleToggleSectionVisibility = async (sectionId, isVisible) => {
    try {
      await api.put(
        `/emergency/${locketId}/section/${sectionId}`,
        { isVisible },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchData();
      showToast("Saved");
    } catch (err) {
      console.error("Toggle section visibility error:", err);
      setError("Failed to update section visibility");
    }
  };

  const handleMoveItem = async (category, index, direction) => {
    const section = sections.find((s) => s.category === category);
    if (!section) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= section.items.length) return;

    const reorderedItems = Array.from(section.items);
    [reorderedItems[index], reorderedItems[newIndex]] = [reorderedItems[newIndex], reorderedItems[index]];

    setSections(
      sections.map((s) => (s.category === category ? { ...s, items: reorderedItems } : s))
    );

    if (shouldUseMockData()) {
      showToast("Reordered");
      return;
    }

    try {
      await api.put(
        `/emergency/${locketId}/section/${category}/reorder-items`,
        { itemOrder: reorderedItems.map((item) => item.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast("Reordered");
    } catch (err) {
      console.error("Move item error:", err);
      await fetchData();
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination, type } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    if (type === "ITEM") {
      const sourceCategory = source.droppableId;
      const destinationCategory = destination.droppableId;
      const sourceSection = sections.find((s) => s.category === sourceCategory);
      const destinationSection = sections.find((s) => s.category === destinationCategory);

      if (!sourceSection || !destinationSection) return;

      const sourceItems = Array.from(sourceSection.items);
      const destinationItems = Array.from(destinationSection.items);
      const [movedItem] = sourceItems.splice(source.index, 1);

      if (!movedItem) return;

      // If moving to a different category
      if (sourceCategory !== destinationCategory) {
        // Add item to destination at the new index
        destinationItems.splice(destination.index, 0, movedItem);

        // Update both sections in state
        const updatedSections = sections.map((s) => {
          if (s.category === sourceCategory) {
            return { ...s, items: sourceItems };
          }
          if (s.category === destinationCategory) {
            return { ...s, items: destinationItems };
          }
          return s;
        });
        setSections(updatedSections);

        if (shouldUseMockData()) {
          showToast("Reordered");
          return;
        }

        try {
          // Update the item's category
          await api.put(
            `/emergency/${locketId}/item/${movedItem.id}`,
            { category: destinationCategory },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Reorder items in destination section
          await api.put(
            `/emergency/${locketId}/section/${destinationCategory}/reorder-items`,
            {
              itemOrder: destinationItems.map((item) => item.id),
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Reorder items in source section (if any items remain)
          if (sourceItems.length > 0) {
            await api.put(
              `/emergency/${locketId}/section/${sourceCategory}/reorder-items`,
              {
                itemOrder: sourceItems.map((item) => item.id),
              },
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );
          }
          showToast("Reordered");
        } catch (err) {
          console.error("Move item between categories error:", err);
          await fetchData();
        }
      } else {
        // Reordering within the same category
        const reorderedItems = Array.from(sourceSection.items);
        const [removed] = reorderedItems.splice(source.index, 1);
        reorderedItems.splice(destination.index, 0, removed);

        const updatedSections = sections.map((s) => (s.category === sourceCategory ? { ...s, items: reorderedItems } : s));
        setSections(updatedSections);

        if (shouldUseMockData()) {
          showToast("Reordered");
          return;
        }

        try {
          await api.put(
            `/emergency/${locketId}/section/${sourceCategory}/reorder-items`,
            {
              itemOrder: reorderedItems.map((item) => item.id),
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          showToast("Reordered");
        } catch (err) {
          console.error("Reorder items error:", err);
          await fetchData();
        }
      }
    }
  };

  if (!token) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-4xl w-full space-y-4">
          <div className="h-16 bg-gray-200 rounded-lg animate-pulse" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        </div>
        <p className="text-gray-500 mt-6">Loading emergency information...</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="h-screen overflow-y-auto overflow-x-hidden snap-y snap-proximity scroll-pt-[80px] bg-gray-50"
    >
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 py-2 flex justify-between items-center">
          <BrandLogo logoClassName="h-7 md:h-8" />
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowProfileModal(true)}
              className="p-2 bg-slate-100 rounded-lg text-slate-700 hover:bg-slate-200 transition-colors duration-150"
              title="Profile"
            >
              <User size={16} />
            </button>
            <button
              onClick={() => navigate(`/${locketId}`)}
              className="p-2 bg-slate-100 rounded-lg text-slate-700 hover:bg-slate-200 transition-colors duration-150"
              title="Home"
            >
              <Home size={16} />
            </button>
            <button
              onClick={() => {
                logout();
                navigate(`/${locketId}`);
              }}
              className="p-2 bg-slate-100 rounded-lg text-slate-700 hover:bg-slate-200 transition-colors duration-150"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-3">
        <div id="info-header" className="snap-start">
          <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <h1 className="text-lg font-bold text-slate-800">{t("emergencyInfo.title")}</h1>
                <p className="text-xs text-slate-500">{t("emergencyInfo.dragReorder")}</p>
              </div>
              <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg shrink-0">{locketId}</span>
            </div>
            <button
              onClick={() => navigate(`/${locketId}/public`)}
              className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors duration-150"
            >
              {t("emergencyInfo.previewPublic")}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
            <p className="text-red-800">❌ {error}</p>
          </div>
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="space-y-3">
            {sections.map((section, index) => (
              <div key={section.id} className="snap-start">
                <SectionCard
                  section={section}
                  sectionIndex={index}
                  onAddItem={handleAddItem}
                  onUpdateItem={handleUpdateItem}
                  onDeleteItem={handleDeleteItem}
                  onToggleSectionVisibility={handleToggleSectionVisibility}
                  onMoveItem={handleMoveItem}
                  onSaved={() => showToast("Saved")}
                  suggestions={fieldSuggestions[section.category] || []}
                  defaultExpanded={index === 0}
                />
              </div>
            ))}
          </div>
        </DragDropContext>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mt-3">
          <p className="text-xs text-blue-800">
            Drag to reorder. Changes save automatically. Medical info shows first.
          </p>
        </div>
      </div>

      <Toast
        message={toast.message}
        visible={toast.visible}
        onDismiss={() => setToast((t) => ({ ...t, visible: false }))}
      />

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        locketId={locketId}
        token={token}
        showToast={showToast}
      />

      {/* Language switcher */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-1">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default EmergencyInfoFormPage;
